import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
} from "recharts";
import { getDetailedClimateColor } from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";

const SolarRadiationAnalysis = ({
  climateInfo,
  solarRadiationData,
  cloudCoverData,
  temperatureData,
}) => {
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loadingAiInterpretation, setLoadingAiInterpretation] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [designSuggestions, setDesignSuggestions] = useState(null);
  const [loadingDesignSuggestions, setLoadingDesignSuggestions] =
    useState(false);
  const [designError, setDesignError] = useState(null);

  if (!climateInfo) {
    return null;
  }

  // Calculate solar radiation performance metrics
  const annualTemp = parseFloat(climateInfo.annualTemp || 0);

  // Calculate radiation from available data or estimate
  const calculateRadiationStats = () => {
    if (solarRadiationData?.data && solarRadiationData.data.length > 0) {
      const data = solarRadiationData.data;

      // Handle different data formats
      let dailyTotals = [];
      if (data[0] && data[0].hour !== undefined) {
        // Hourly data - convert to daily
        const dailyGroups = {};
        data.forEach((item) => {
          const day = item.day_of_year || Math.floor(item.hour / 24);
          if (!dailyGroups[day]) dailyGroups[day] = [];
          dailyGroups[day].push(item.global_horizontal_rad || item.mean || 0);
        });

        dailyTotals = Object.values(dailyGroups).map((hours) =>
          hours.reduce((sum, val) => sum + val, 0)
        );
      } else {
        // Daily data
        dailyTotals = data.map(
          (item) => item.mean || item.global_horizontal_rad || 0
        );
      }

      const avgDaily =
        dailyTotals.reduce((sum, val) => sum + val, 0) / dailyTotals.length;
      const maxDaily = Math.max(...dailyTotals);
      const minDaily = Math.min(...dailyTotals);

      return { avgDaily, maxDaily, minDaily };
    }

    // Fallback estimation based on climate
    const baseRadiation = 4500 + annualTemp * 15;
    return {
      avgDaily: baseRadiation,
      maxDaily: baseRadiation * 1.8,
      minDaily: baseRadiation * 0.3,
    };
  };

  const radiationStats = calculateRadiationStats();
  const avgDailyRadiation = radiationStats.avgDaily;
  const maxDailyRadiation = radiationStats.maxDaily;
  const minDailyRadiation = radiationStats.minDaily;
  const radiationRange = maxDailyRadiation - minDailyRadiation;

  // Calculate PV potential (kWh/kWp/year) - typical conversion
  const pvPotential = (avgDailyRadiation * 365) / 1000; // Convert Wh to kWh

  // Calculate seasonal radiation patterns
  const winterRadiation = avgDailyRadiation * 0.6; // Typical winter reduction
  const springRadiation = avgDailyRadiation * 1.1;
  const summerRadiation = avgDailyRadiation * 1.4;
  const fallRadiation = avgDailyRadiation * 0.9;

  // Calculate high solar days (>5000 Wh/m²/day is considered high)
  const highSolarThreshold = 5000;
  const highSolarDays =
    365 *
    (avgDailyRadiation > highSolarThreshold
      ? 0.8
      : avgDailyRadiation > 3000
      ? 0.5
      : 0.2);
  const highSolarPercentage = (highSolarDays / 365) * 100;

  // Generate synthetic solar radiation data for graph
  const generateSolarData = () => {
    const data = [];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const seasonalMultipliers = [
      0.6, 0.75, 0.9, 1.1, 1.3, 1.4, 1.4, 1.3, 1.1, 0.9, 0.7, 0.6,
    ];

    for (let i = 0; i < 12; i++) {
      const monthlyRadiation =
        avgDailyRadiation *
        seasonalMultipliers[i] *
        (0.9 + Math.random() * 0.2);
      data.push({
        month: months[i],
        radiation: monthlyRadiation,
        pvOutput: (monthlyRadiation * 30) / 1000, // Monthly kWh/kWp
        dayOfYear: (i + 1) * 30,
        originalIndex: i,
      });
    }
    return data;
  };

  const solarData = generateSolarData();

  return (
    <div
      id="solar-radiation-analysis-section"
      className="min-h-screen bg-gray-100 px-14 py-8 pt-28"
    >
      {/* Solar Radiation Heading */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 tracking-wide">
          Solar Radiation & Energy
        </h1>
      </div>

      {/* Three Cards Layout - Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* MACRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem]">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MACRO
            </h3>
          </div>

          {/* Top section - solar radiation metrics */}
          <div className="flex justify-between items-start mb-8">
            {/* Left side - Max and Min radiation */}
            <div className="flex-1">
              <div className="mb-4">
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Peak Radiation
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {Math.round(maxDailyRadiation)} Wh/m²
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Minimum Radiation
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {Math.round(minDailyRadiation)} Wh/m²
                </div>
              </div>
            </div>

            {/* Right side - Daily average solar radiation */}
            <div className="flex-1 text-right">
              <div className="text-5xl font-black text-black mb-2">
                {avgDailyRadiation.toFixed(0)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Wh/m² Daily Avg.
              </div>
            </div>
          </div>

          {/* Bottom section - Classification information */}
          <div>
            {/* Large Köppen classification code */}
            <div
              className="text-6xl font-black leading-none mb-4"
              style={{
                color: getDetailedClimateColor(
                  climateInfo.mainClass,
                  climateInfo.subClass
                ),
              }}
            >
              {climateInfo.mainClass}
              {climateInfo.subClass}
            </div>

            {/* Classification label */}
            <div className="text-sm text-gray-500 font-bold mb-4">
              Köppen-Geiger Climate Classification
            </div>

            {/* Climate name */}
            <div className="font-bold text-gray-800 mb-4 text-lg">
              {climateInfo.fullName}
            </div>

            {/* Description */}
            <div className="text-sm text-gray-500 leading-relaxed font-bold">
              <div>
                {avgDailyRadiation >= 6000
                  ? "Exceptional solar energy potential with year-round high radiation levels ideal for renewable energy systems."
                  : avgDailyRadiation >= 4500
                  ? "Strong solar energy potential suitable for efficient photovoltaic and thermal solar applications."
                  : avgDailyRadiation >= 3000
                  ? "Moderate solar potential that can support solar energy systems with proper design optimization."
                  : "Limited solar potential requiring careful system sizing and potentially hybrid renewable energy approaches."}
              </div>
            </div>
          </div>
        </div>

        {/* MICRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MICRO
            </h3>
          </div>

          {/* PV Potential at top */}
          <div className="text-left mb-8">
            <div className="text-5xl font-black text-black mb-2">
              {Math.round(pvPotential)}
            </div>
            <div className="text-sm text-gray-500 font-bold">
              kWh/kWp/year PV
            </div>
          </div>

          {/* Seasonal Solar Patterns */}
          <div className="mb-8 flex-grow">
            <h4 className="font-bold text-gray-500 mb-4 text-lg">
              Seasonal Patterns
            </h4>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Winter:</div>
                  <div className="font-bold text-gray-500">
                    {Math.round(winterRadiation)} Wh/m²
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Spring:</div>
                  <div className="font-bold text-gray-500">
                    {Math.round(springRadiation)} Wh/m²
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Summer:</div>
                  <div className="font-bold text-gray-500">
                    {Math.round(summerRadiation)} Wh/m²
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Fall:</div>
                  <div className="font-bold text-gray-500">
                    {Math.round(fallRadiation)} Wh/m²
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Solar Extremes */}
          <div className="flex justify-between items-center mt-auto">
            <div className="text-left">
              <div className="text-4xl font-black text-black">
                {Math.round(minDailyRadiation)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Wh/m² minimum
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-black">
                {Math.round(radiationRange)}
              </div>
              <div className="text-sm text-gray-500 font-bold">Wh/m² range</div>
            </div>
          </div>
        </div>

        {/* DESIGN Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              DESIGN
            </h3>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white rounded-sm"></div>
            </div>
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 bg-white transform rotate-45"></div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            {/* Conditional content: Show loading, AI suggestions, error, or static text */}
            {loadingDesignSuggestions ? (
              <div className="text-sm text-gray-700 space-y-2">
                <style jsx>{`
                  @keyframes solarWave1 {
                    0%,
                    100% {
                      background-color: #d1d5db;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  @keyframes solarWave2 {
                    0%,
                    100% {
                      background-color: #9ca3af;
                    }
                    50% {
                      background-color: #d1d5db;
                    }
                  }
                  @keyframes solarWave3 {
                    0%,
                    100% {
                      background-color: #e5e7eb;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  .animate-solar-wave-1 {
                    animation: solarWave1 2s ease-in-out infinite;
                  }
                  .animate-solar-wave-2 {
                    animation: solarWave2 2s ease-in-out infinite 0.3s;
                  }
                  .animate-solar-wave-3 {
                    animation: solarWave3 2s ease-in-out infinite 0.6s;
                  }
                `}</style>
                <div className="h-3 rounded animate-solar-wave-1"></div>
                <div className="h-3 rounded animate-solar-wave-2 w-4/5"></div>
                <div className="h-3 rounded animate-solar-wave-3 w-3/5"></div>
                <div className="h-3 rounded animate-solar-wave-1 w-5/6"></div>
                <div className="h-3 rounded animate-solar-wave-2 w-2/3"></div>
                <div className="h-3 rounded animate-solar-wave-3"></div>
              </div>
            ) : designSuggestions ? (
              <div className="text-sm text-gray-500 leading-relaxed font-bold whitespace-pre-wrap">
                {designSuggestions}
              </div>
            ) : designError ? (
              <div className="text-red-600 text-sm font-bold">
                Error: {designError}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This location has{" "}
                  {avgDailyRadiation >= 6000
                    ? "excellent solar energy potential ideal for large-scale photovoltaic systems and solar thermal applications"
                    : avgDailyRadiation >= 4000
                    ? "good solar potential suitable for efficient renewable energy systems and passive solar heating strategies"
                    : avgDailyRadiation >= 2500
                    ? "moderate solar access benefiting from optimized PV placement and solar thermal water heating systems"
                    : "limited solar potential requiring enhanced system efficiency and strategic positioning for viable renewable energy"}{" "}
                  with {Math.round(avgDailyRadiation)} Wh/m² daily radiation.
                </p>
                <p>
                  The {Math.round(radiationRange)} Wh/m² seasonal variation{" "}
                  {radiationRange > 3000
                    ? "requires flexible solar system sizing and seasonal energy storage strategies"
                    : "enables consistent year-round solar energy generation with minimal seasonal adjustment"}{" "}
                  supporting {Math.round(pvPotential)} kWh/kWp annual PV output
                  with {highSolarPercentage.toFixed(1)}% high solar radiation
                  days.
                </p>
              </div>
            )}

            {/* Design Suggestion Text and AI Button */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-black text-sm font-bold">
                Design Suggestion
              </span>
              <button
                className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                onClick={async () => {
                  if (!OPENROUTER_API_KEY) {
                    setDesignError("OpenRouter API key not configured");
                    return;
                  }

                  setDesignSuggestions(null);
                  setDesignError(null);
                  setLoadingDesignSuggestions(true);

                  try {
                    const response = await fetch(
                      "https://openrouter.ai/api/v1/chat/completions",
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                          "HTTP-Referer": window.location.origin,
                          "X-Title": "ClimaPro.AI",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          model: "anthropic/claude-3.7-sonnet",
                          route: "fallback",
                          messages: [
                            {
                              role: "system",
                              content:
                                "You are an expert building design consultant specializing in solar energy systems, photovoltaic design, and renewable energy integration in architecture. Analyze the solar radiation metrics provided to give location-specific building design recommendations. Structure your response as exactly 3 paragraphs: 1) Solar energy system strategy, 2) Building orientation and envelope approach, 3) Energy storage and grid integration priorities. Include specific radiation numbers and PV potential in your reasoning. Keep total response under 75 words with actionable, solar energy-focused design advice.",
                            },
                            {
                              role: "user",
                              content: `For this ${climateInfo.mainClass}${
                                climateInfo.subClass
                              } (${
                                climateInfo.fullName
                              }) climate location, provide targeted building design recommendations based on these solar energy metrics:

SOLAR RADIATION & ENERGY PROFILE:
- Daily Average Radiation: ${Math.round(avgDailyRadiation)} Wh/m²
- Peak Radiation: ${Math.round(maxDailyRadiation)} Wh/m²
- Minimum Radiation: ${Math.round(minDailyRadiation)} Wh/m²
- Radiation Range: ${Math.round(radiationRange)} Wh/m²
- PV Potential: ${Math.round(pvPotential)} kWh/kWp/year
- Winter Radiation: ${Math.round(winterRadiation)} Wh/m²
- Summer Radiation: ${Math.round(summerRadiation)} Wh/m²
- High Solar Days: ${highSolarPercentage.toFixed(1)}%

Based on this solar radiation data, provide design recommendations with clear reasoning:

1. SOLAR ENERGY SYSTEMS: Explain photovoltaic sizing and solar thermal strategies based on the ${Math.round(
                                avgDailyRadiation
                              )} Wh/m² radiation and ${Math.round(
                                pvPotential
                              )} kWh/kWp potential. Reference these numbers in your reasoning.

2. BUILDING ORIENTATION: Justify building positioning and envelope design based on the ${Math.round(
                                radiationRange
                              )} Wh/m² seasonal variation and ${Math.round(
                                maxDailyRadiation
                              )} Wh/m² peak radiation. Use these specific values in your explanation.

3. ENERGY STORAGE: Recommend battery systems and grid integration based on the ${Math.round(
                                winterRadiation
                              )} Wh/m² winter and ${Math.round(
                                summerRadiation
                              )} Wh/m² summer radiation patterns. Explain why these specific solar numbers drive your recommendations.

Use the actual solar values (${Math.round(
                                avgDailyRadiation
                              )} Wh/m² avg, ${Math.round(
                                pvPotential
                              )} kWh/kWp/year, ${highSolarPercentage.toFixed(
                                1
                              )}% high solar days) to justify each design decision.`,
                            },
                          ],
                        }),
                      }
                    );

                    if (!response.ok) {
                      if (response.status === 503) {
                        throw new Error("AI service temporarily unavailable");
                      } else if (response.status === 401) {
                        throw new Error("Authentication failed");
                      } else if (response.status === 429) {
                        throw new Error("Rate limit exceeded");
                      }
                      throw new Error(`Request failed: ${response.status}`);
                    }

                    const data = await response.json();

                    if (
                      data.choices &&
                      data.choices[0] &&
                      data.choices[0].message
                    ) {
                      setDesignSuggestions(data.choices[0].message.content);
                    } else if (data.error) {
                      throw new Error(data.error.message || "Unknown AI error");
                    }
                  } catch (error) {
                    console.error(
                      "AI solar radiation suggestions error:",
                      error
                    );
                    setDesignError(error.message);
                  } finally {
                    setLoadingDesignSuggestions(false);
                  }
                }}
                disabled={loadingDesignSuggestions}
              >
                {loadingDesignSuggestions ? "..." : "ClimaPro.AI"}
              </button>
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <div className="text-sm text-gray-600 mb-1">+ PHOTOVOLTAIC</div>
              <div className="text-sm text-gray-600">+ SOLAR THERMAL</div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">
                {highSolarPercentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                High Solar Days
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solar Radiation Graph Card - Bottom Section */}
      <div className="bg-gray-100 border border-gray-500 rounded-[2rem] overflow-hidden">
        <button
          className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-200 transition-colors"
          onClick={() => {
            const graphContent = document.getElementById("solar-graph-content");
            const chevron = document.getElementById("solar-graph-chevron");
            if (
              graphContent.style.display === "none" ||
              !graphContent.style.display
            ) {
              graphContent.style.display = "block";
              chevron.style.transform = "rotate(180deg)";
            } else {
              graphContent.style.display = "none";
              chevron.style.transform = "rotate(0deg)";
            }
          }}
        >
          <div className="flex items-center space-x-4 ml-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              GRAPH DATA
            </h3>
            <button
              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
              onClick={async (e) => {
                e.stopPropagation();

                if (!OPENROUTER_API_KEY) {
                  setAiError("OpenRouter API key not configured");
                  return;
                }

                // Clear previous results before starting new analysis
                setAiInterpretation(null);
                setAiError(null);
                setLoadingAiInterpretation(true);

                try {
                  // Calculate solar radiation statistics
                  const solarStats = {
                    avgDaily: Math.round(avgDailyRadiation),
                    maxDaily: Math.round(maxDailyRadiation),
                    minDaily: Math.round(minDailyRadiation),
                    radiationRange: Math.round(radiationRange),
                    pvPotential: Math.round(pvPotential),
                    winterRadiation: Math.round(winterRadiation),
                    springRadiation: Math.round(springRadiation),
                    summerRadiation: Math.round(summerRadiation),
                    fallRadiation: Math.round(fallRadiation),
                  };

                  const response = await fetch(
                    "https://openrouter.ai/api/v1/chat/completions",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                        "HTTP-Referer": window.location.origin,
                        "X-Title": "ClimaPro.AI",
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        model: "anthropic/claude-3.7-sonnet",
                        route: "fallback",
                        messages: [
                          {
                            role: "system",
                            content:
                              "You are an expert climate data analyst specializing in solar radiation and photovoltaic potential graph interpretation. Focus exclusively on analyzing solar radiation patterns, PV output trends, seasonal variations, and energy generation potential. Provide insights specifically about what the solar radiation and PV graphs reveal, not general climate advice. Keep responses concise, 100 words, focusing on graph-specific observations.",
                          },
                          {
                            role: "user",
                            content: `Analyze the solar radiation and PV potential graph patterns for this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} (${
                              climateInfo.fullName
                            }) climate location:

ANNUAL STATISTICS:
- Daily Average Radiation: ${solarStats.avgDaily} Wh/m²
- Peak Daily Radiation: ${solarStats.maxDaily} Wh/m²
- Minimum Daily Radiation: ${solarStats.minDaily} Wh/m²
- Total Radiation Range: ${solarStats.radiationRange} Wh/m²
- Annual PV Potential: ${solarStats.pvPotential} kWh/kWp/year

SEASONAL PATTERNS:
- Winter (Dec-Feb): ${solarStats.winterRadiation} Wh/m² average
- Spring (Mar-May): ${solarStats.springRadiation} Wh/m² average
- Summer (Jun-Aug): ${solarStats.summerRadiation} Wh/m² average
- Fall (Sep-Nov): ${solarStats.fallRadiation} Wh/m² average

SEASONAL RADIATION RANGES:
- Winter-Summer difference: ${
                              solarStats.summerRadiation -
                              solarStats.winterRadiation
                            } Wh/m²
- Spring-Fall difference: ${
                              solarStats.springRadiation -
                              solarStats.fallRadiation
                            } Wh/m²

Analyze specifically what these solar radiation and PV potential graphs reveal about:
1. Seasonal solar radiation transition patterns and energy availability timing
2. Solar radiation variability and PV generation potential within this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} classification
3. Peak and minimum solar energy patterns visible in the graph
4. Relationship between solar radiation input and PV output potential
5. Notable solar radiation extremes or energy generation patterns shown in the data

Focus only on what the solar radiation and PV potential graph data and patterns show, not design recommendations.`,
                          },
                        ],
                      }),
                    }
                  );

                  if (!response.ok) {
                    if (response.status === 503) {
                      throw new Error("AI service temporarily unavailable");
                    } else if (response.status === 401) {
                      throw new Error("Authentication failed");
                    } else if (response.status === 429) {
                      throw new Error("Rate limit exceeded");
                    }
                    throw new Error(`Request failed: ${response.status}`);
                  }

                  const data = await response.json();

                  if (
                    data.choices &&
                    data.choices[0] &&
                    data.choices[0].message
                  ) {
                    setAiInterpretation(data.choices[0].message.content);
                  } else if (data.error) {
                    throw new Error(data.error.message || "Unknown AI error");
                  }
                } catch (error) {
                  console.error(
                    "AI solar radiation interpretation error:",
                    error
                  );
                  setAiError(error.message);
                } finally {
                  setLoadingAiInterpretation(false);
                }
              }}
              disabled={loadingAiInterpretation}
            >
              {loadingAiInterpretation ? "..." : "ClimaPro.AI"}
            </button>
          </div>
          <svg
            id="solar-graph-chevron"
            className="w-8 h-8 text-gray-600 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div
          id="solar-graph-content"
          style={{ display: "none" }}
          className="px-8 pb-8"
        >
          <>
            {/* Conditional Layout: Split when AI content exists, full width otherwise */}
            {aiInterpretation || aiError || loadingAiInterpretation ? (
              <div className="flex gap-6">
                {/* AI Interpretation Section - Left 30% */}
                <div className="w-[30%] bg-gray-100">
                  <div className="h-full">
                    <h4 className="font-bold text-gray-800 mb-4">
                      AI Solar Radiation Analysis
                    </h4>
                    {loadingAiInterpretation && (
                      <div className="space-y-3">
                        {/* Skeleton loading rectangles with color animation */}
                        <style jsx>{`
                          @keyframes solarColorWave1 {
                            0%,
                            100% {
                              background-color: #d1d5db;
                            }
                            50% {
                              background-color: #9ca3af;
                            }
                          }
                          @keyframes solarColorWave2 {
                            0%,
                            100% {
                              background-color: #9ca3af;
                            }
                            50% {
                              background-color: #d1d5db;
                            }
                          }
                          @keyframes solarColorWave3 {
                            0%,
                            100% {
                              background-color: #e5e7eb;
                            }
                            50% {
                              background-color: #9ca3af;
                            }
                          }
                          .animate-solar-wave-1 {
                            animation: solarColorWave1 2s ease-in-out infinite;
                          }
                          .animate-solar-wave-2 {
                            animation: solarColorWave2 2s ease-in-out infinite
                              0.3s;
                          }
                          .animate-solar-wave-3 {
                            animation: solarColorWave3 2s ease-in-out infinite
                              0.6s;
                          }
                        `}</style>
                        <div className="h-3 rounded animate-solar-wave-1"></div>
                        <div className="h-3 rounded animate-solar-wave-2 w-4/5"></div>
                        <div className="h-3 rounded animate-solar-wave-3 w-3/5"></div>
                        <div className="h-3 rounded animate-solar-wave-1 w-5/6"></div>
                        <div className="h-3 rounded animate-solar-wave-2 w-2/3"></div>
                        <div className="h-3 rounded animate-solar-wave-3"></div>
                        <div className="h-3 rounded animate-solar-wave-1 w-3/4"></div>
                        <div className="h-3 rounded animate-solar-wave-2 w-4/5"></div>
                        <div className="h-3 rounded animate-solar-wave-3 w-1/2"></div>
                        <div className="h-3 rounded animate-solar-wave-1 w-3/5"></div>
                        <div className="h-3 rounded animate-solar-wave-2 w-5/6"></div>
                        <div className="h-3 rounded animate-solar-wave-3 w-2/3"></div>
                      </div>
                    )}
                    {aiError && (
                      <div className="text-red-600 text-sm font-bold">
                        Error: {aiError}
                      </div>
                    )}
                    {aiInterpretation && (
                      <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap font-bold">
                        {aiInterpretation}
                      </div>
                    )}
                  </div>
                </div>

                {/* Graph Section - Right 70% when AI content exists */}
                <div className="w-[70%] h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={solarData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          label={{
                            value: "Wh/m²",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          domain={[0, "dataMax + 1000"]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                          formatter={(value, name) => [
                            name === "radiation"
                              ? `${Math.round(value)} Wh/m²`
                              : `${value.toFixed(1)} kWh/kWp`,
                            name === "radiation"
                              ? "Solar Radiation"
                              : "PV Output",
                          ]}
                        />

                        <ReferenceLine
                          y={3000}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />
                        <ReferenceLine
                          y={5000}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />

                        <Line
                          type="monotone"
                          dataKey="radiation"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          dot={false}
                          name="radiation"
                        />
                        <Line
                          type="monotone"
                          dataKey="pvOutput"
                          stroke="#ef4444"
                          strokeWidth={2}
                          dot={false}
                          name="pvOutput"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center space-x-8 mt-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-amber-500 mr-3"></div>
                      <span className="text-gray-700">Solar Radiation</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-red-500 mr-3"></div>
                      <span className="text-gray-700">PV Output</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                      <span className="text-gray-700">
                        Performance Thresholds
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Full Width Graph - When no AI content */
              <div className="h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={solarData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                        label={{
                          value: "Wh/m²",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        domain={[0, "dataMax + 1000"]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "8px",
                        }}
                        formatter={(value, name) => [
                          name === "radiation"
                            ? `${Math.round(value)} Wh/m²`
                            : `${value.toFixed(1)} kWh/kWp`,
                          name === "radiation"
                            ? "Solar Radiation"
                            : "PV Output",
                        ]}
                      />

                      <ReferenceLine
                        y={3000}
                        stroke="#e0e0e0"
                        strokeDasharray="2 2"
                      />
                      <ReferenceLine
                        y={5000}
                        stroke="#e0e0e0"
                        strokeDasharray="2 2"
                      />

                      <Line
                        type="monotone"
                        dataKey="radiation"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        name="radiation"
                      />
                      <Line
                        type="monotone"
                        dataKey="pvOutput"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={false}
                        name="pvOutput"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex justify-center space-x-8 mt-4 text-sm">
                  <div className="flex items-center">
                    <div className="w-6 h-0.5 bg-amber-500 mr-3"></div>
                    <span className="text-gray-700">Solar Radiation</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-0.5 bg-red-500 mr-3"></div>
                    <span className="text-gray-700">PV Output</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                    <span className="text-gray-700">
                      Performance Thresholds
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default SolarRadiationAnalysis;
